import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { createMarketResearch } from '@/api/business/marketResearch' 
import { Checkbox } from '@/components/ui/checkbox'
import Button from '@/components/Button'
import { Input } from '@/components/ui/input'  
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/ui/form'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod' 
import { CalendarIcon } from 'lucide-react'
import { Calendar } from "@/components/ui/calendar"
import { cn } from "@/lib/utils"
import { format } from 'date-fns'
import { useResaleQuery } from '@/queries/useResaleQuery'
import { MarketResearchKeys } from '@/queries/useMarketResearchQuery'

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
}

const AddDialog = ({ isOpen, onClose }: AddDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast() 

    const { data: DataResale } = useResaleQuery()

    const schema = z.object({
        NM_PESQUISA: z.string().min(1, {
            message: 'O campo nome da pesquisa é obrigatório',
        }).max(150, 'Quantidade máxima de 150 caracteres'),
        SK_REVENDA: z.number().min(1, {
            message: 'O campo Revenda é obrigatório',
        }),
        SK_TEMPO_INICIO: z.date({
            required_error: "Data de inicio é obrigatório",
        }),
        SK_TEMPO_FIM: z.date({
            required_error: "Data final é obrigatório",
        }),
        DD_TIPO_CICLO: z.string().min(1, {
            message: 'O campo tipo cliclo é obrigatório',
        }),
        DD_TIPO_ENCERRAMENTO: z.string().min(1, {
            message: 'O campo tipo encerramento é obrigatório',
        }),
        QTD_DIAS_CARENCIA: z.string(),
        DD_COLETA_IMAGEM: z.number()
    });

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            NM_PESQUISA: '',
            SK_REVENDA: 0,
            SK_TEMPO_INICIO: new Date(),
            SK_TEMPO_FIM: new Date(),
            DD_TIPO_CICLO: '',
            DD_TIPO_ENCERRAMENTO: '',
            QTD_DIAS_CARENCIA: '0',
            DD_COLETA_IMAGEM: 0
        },
    })

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: createMarketResearch,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: MarketResearchKeys.lists() })
            onClose()
            form.reset()
            toast({
                title: 'Pesquisa criada com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível criar a pesquisa. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar a pesquisa',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => {  
        mutate({ 
            NM_PESQUISA: data.NM_PESQUISA,
            SK_REVENDA: data.SK_REVENDA,
            SK_TEMPO_INICIO: Number(data.SK_TEMPO_INICIO.toISOString().slice(0, 10).replace(/-/g, '')),
            SK_TEMPO_FIM: Number(data.SK_TEMPO_FIM.toISOString().slice(0, 10).replace(/-/g, '')),
            DD_COLETA_IMAGEM: data.DD_COLETA_IMAGEM,
            DD_TIPO_ENCERRAMENTO: data.DD_TIPO_ENCERRAMENTO,
            QTD_DIAS_CARENCIA: Number(data.QTD_DIAS_CARENCIA),
            DD_TIPO_CICLO: data.DD_TIPO_CICLO
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[600px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Criar nova pesquisa</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        <div className='grid grid-cols-[1fr] gap-4'>
                            <FormField
                                name="NM_PESQUISA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nome da pesquisa</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div> 
                        <div className='grid grid-cols-[1fr,1fr] gap-4'> 
                            <FormField
                                name="SK_REVENDA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Revenda</FormLabel> 
                                        <Select onValueChange={(value) => field.onChange(Number(value))} defaultValue={field.value.toString()}>
                                            <FormControl>
                                                <SelectTrigger>
                                                <SelectValue placeholder="" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {DataResale?.map((option) => (  
                                                    <SelectItem key={option.SK_REVENDA} value={option.SK_REVENDA.toString()}>{option.DS_REVENDA}</SelectItem> 
                                                ))} 
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_TIPO_CICLO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Tipo ciclo</FormLabel>
                                        <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                <SelectValue placeholder="" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value='Diário'>Diário</SelectItem>
                                                <SelectItem value="Semanal">Semanal</SelectItem>
                                                <SelectItem value="Quinzenal">Quinzenal</SelectItem>
                                                <SelectItem value="Mensal">Mensal</SelectItem>
                                                <SelectItem value="Período">Período</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>  
                        <div className='grid md:grid-cols-2 grid-cols-1 gap-4'>  
                            <FormField
                                name="SK_TEMPO_INICIO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                        <FormLabel>Data de inicio</FormLabel>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                            <FormControl>
                                                <Button
                                                variant={"outline"}
                                                className={cn(
                                                    "w-full pl-3 text-left font-normal",
                                                    !field.value && "text-muted-foreground"
                                                )}
                                                >
                                                {field.value ? (
                                                    format(field.value, "PPP")
                                                ) : (
                                                    <span>Selecionar Data</span>
                                                )}
                                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                </Button>
                                            </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={field.value}
                                                onSelect={field.onChange} 
                                                initialFocus
                                            />
                                            </PopoverContent>
                                        </Popover> 
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                name="SK_TEMPO_FIM"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                    <FormLabel>Data final</FormLabel>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                        <FormControl>
                                            <Button
                                            variant={"outline"}
                                            className={cn(
                                                "w-full pl-3 text-left font-normal",
                                                !field.value && "text-muted-foreground"
                                            )}
                                            >
                                            {field.value ? (
                                                format(field.value, "PPP")
                                            ) : (
                                                <span>Selecionar Data</span>
                                            )}
                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                            </Button>
                                        </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={field.value}
                                            onSelect={field.onChange} 
                                            initialFocus
                                        />
                                        </PopoverContent>
                                    </Popover> 
                                    <FormMessage />
                                </FormItem>
                                )}
                            />  
                        </div> 
                         <div className='grid grid-cols-[1fr,1fr] gap-4'> 
                            <FormField
                                name="DD_TIPO_ENCERRAMENTO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Tipo encerramento</FormLabel>
                                        <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                <SelectValue placeholder="" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value='Manual'>Manual</SelectItem>
                                                <SelectItem value="Automático">Automático</SelectItem> 
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            {
                                form.watch('DD_TIPO_ENCERRAMENTO') == 'Automático' && (
                                    <FormField
                                        name="QTD_DIAS_CARENCIA"
                                        control={form.control}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Dias de carência</FormLabel>
                                                <FormControl>
                                                    <Input {...field} type='number' />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    /> 
                                )}
                        </div> 
                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name="DD_COLETA_IMAGEM"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                        <FormControl>
                                            <Checkbox
                                                checked={Boolean(field.value)}
                                                onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                                            />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                            <FormLabel>
                                                Inclusão de foto obrigatório?
                                            </FormLabel> 
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />
                            </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AddDialog
